import { Track } from '../types/album';

export interface iTunesTrackResult {
  trackId: number;
  trackName: string;
  artistName: string;
  collectionName: string;
  previewUrl?: string;
  trackTimeMillis: number;
  trackNumber: number;
}

export interface iTunesSearchResult {
  results: iTunesTrackResult[];
  resultCount: number;
}

class iTunesService {
  private lastRequestTime: number = 0;
  private readonly baseUrl = 'https://itunes.apple.com/search';

  private async respectRateLimit(): Promise<void> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    const minDelay = 3000; // 3 seconds between requests (iTunes API allows ~20/minute)
    
    if (timeSinceLastRequest < minDelay) {
      const delayNeeded = minDelay - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, delayNeeded));
    }
    
    this.lastRequestTime = Date.now();
  }

  private async makeiTunesRequest(url: string, retryCount: number = 0): Promise<any> {
    // Respect rate limiting
    await this.respectRateLimit();

    try {
      const response = await fetch(url, {
        headers: {
          'User-Agent': 'DiskcatalogApp/1.0'
        }
      });

      if (response.status === 429 || response.status === 503) {
        // Rate limited or service unavailable - implement exponential backoff
        if (retryCount < 2) {
          const backoffDelay = Math.pow(2, retryCount) * 5000; // 5s, 10s
          console.log(`iTunes API rate limited, retrying in ${backoffDelay}ms (attempt ${retryCount + 1}/2)`);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
          return this.makeiTunesRequest(url, retryCount + 1);
        } else {
          console.error('Max retries exceeded for iTunes API rate limiting');
          return null;
        }
      }

      if (!response.ok) {
        console.error(`iTunes API request failed: ${response.status} ${response.statusText}`);
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('iTunes API request failed:', error);
      return null;
    }
  }

  async searchAlbum(artist: string, album: string): Promise<iTunesTrackResult[]> {
    // Try multiple search strategies, starting with the most specific
    const strategies = [
      // Strategy 1: Full artist + album search
      `${artist} ${album}`,
      // Strategy 2: Artist only (for hard to match album names)
      artist,
      // Strategy 3: Simplified artist + key words from album
      `${artist} ${this.extractKeyWords(album)}`
    ];

    for (const [index, searchTerm] of strategies.entries()) {
      const searchUrl = `${this.baseUrl}?term=${encodeURIComponent(searchTerm)}&entity=song&limit=50`;
      console.log(`iTunes search strategy ${index + 1}: "${searchTerm}"`);
      
      const searchResults: iTunesSearchResult = await this.makeiTunesRequest(searchUrl);
      
      if (!searchResults?.results?.length) {
        console.log(`No iTunes results for strategy ${index + 1}`);
        continue;
      }

      // Filter results to find tracks from the specific album
      const albumTracks = searchResults.results.filter(track => {
        const trackAlbum = track.collectionName.toLowerCase();
        const trackArtist = track.artistName.toLowerCase();
        const targetAlbum = album.toLowerCase();
        const targetArtist = artist.toLowerCase();
        
        // Check if artist matches
        const artistMatch = trackArtist.includes(targetArtist) || 
                           targetArtist.includes(trackArtist) ||
                           this.normalizeArtistName(trackArtist) === this.normalizeArtistName(targetArtist);
        
        if (!artistMatch) return false;

        // For first strategy, require album name similarity
        if (index === 0) {
          return this.albumNamesMatch(trackAlbum, targetAlbum);
        }
        
        // For other strategies, be more flexible but still try to match album
        return this.albumNamesMatch(trackAlbum, targetAlbum);
      });

      if (albumTracks.length > 0) {
        console.log(`Found ${albumTracks.length} tracks for "${album}" by ${artist} on iTunes (strategy ${index + 1})`);
        return albumTracks;
      }
    }

    console.log(`No iTunes tracks found for: ${artist} - ${album}`);
    return [];
  }

  private extractKeyWords(album: string): string {
    // Extract meaningful words from album title, removing common words
    const commonWords = ['the', 'and', 'or', 'of', 'to', 'in', 'a', 'an', 'is', 'that', 'what', 'im', 'not'];
    return album
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !commonWords.includes(word))
      .slice(0, 3) // Take first 3 meaningful words
      .join(' ');
  }

  private normalizeArtistName(artist: string): string {
    return artist
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private albumNamesMatch(trackAlbum: string, targetAlbum: string): boolean {
    const normalize = (name: string) => name
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    const normalizedTrack = normalize(trackAlbum);
    const normalizedTarget = normalize(targetAlbum);
    
    // Direct match
    if (normalizedTrack === normalizedTarget) return true;
    
    // Contains match (either direction) - but require reasonable length
    if (normalizedTarget.length >= 4 && normalizedTrack.length >= 4) {
      if (normalizedTrack.includes(normalizedTarget) || normalizedTarget.includes(normalizedTrack)) {
        return true;
      }
    }
    
    // Word overlap match - requires at least 75% of words in common for shorter titles
    // or at least 3 words in common for longer titles
    const trackWords = normalizedTrack.split(' ').filter(w => w.length > 2);
    const targetWords = normalizedTarget.split(' ').filter(w => w.length > 2);
    const commonWords = trackWords.filter(word => targetWords.includes(word));
    
    const minWords = Math.min(trackWords.length, targetWords.length);
    const requiredOverlap = minWords <= 3 ? 0.75 : 0.6; // Higher threshold for short titles
    
    return commonWords.length >= Math.max(2, minWords * requiredOverlap);
  }

  private calculateAlbumConfidence(originalTracks: Track[], itunesTracks: iTunesTrackResult[]): { score: number; highConfidence: boolean } {
    let score = 0;
    // const maxScore = 100; // 25 + 45 + 25 + 5

    // Bonus track detection and core track matching
    const originalLength = originalTracks.length;
    const itunesLength = itunesTracks.length;
    const coreTrackCount = Math.min(originalLength, itunesLength);
    const trackCountDiff = Math.abs(originalLength - itunesLength);
    const hasExtraTracks = itunesLength > originalLength;
    
    // Check how well the core album (first N tracks) matches using the SAME logic as actual matching
    let coreMatches = 0;
    const matchDetails: string[] = [];
    const usedItunesTracks = new Set<number>(); // Track which iTunes tracks we've matched to avoid double-matching
    
    for (let i = 0; i < coreTrackCount; i++) {
      const originalTrack = originalTracks[i];
      
      // Use the SAME find() logic as the actual track matching
      const matchingItunesTrack = itunesTracks.find((itunesTrack, itunesIndex) => {
        // Skip if this iTunes track was already matched
        if (usedItunesTracks.has(itunesIndex)) return false;
        return this.trackTitlesMatch(originalTrack.title, itunesTrack.trackName, i + 1, itunesTrack.trackNumber);
      });
      
      if (matchingItunesTrack) {
        coreMatches++;
        const itunesIndex = itunesTracks.indexOf(matchingItunesTrack);
        usedItunesTracks.add(itunesIndex);
        matchDetails.push(`✓ "${originalTrack.title}" -> "${matchingItunesTrack.trackName}"`);
      } else {
        matchDetails.push(`✗ "${originalTrack.title}" -> no match found`);
      }
    }
    
    // Log the first few mismatches for debugging
    const mismatches = matchDetails.filter(d => d.startsWith('✗')).slice(0, 3);
    if (mismatches.length > 0) {
      console.log(`Confidence matching details:`, mismatches);
    }
    
    const coreMatchRate = coreMatches / coreTrackCount;

    // Track count similarity with bonus track intelligence (25 points)
    if (trackCountDiff === 0) {
      score += 25; // Perfect match
    } else if (hasExtraTracks && trackCountDiff <= 3 && coreMatchRate >= 0.7) {
      // iTunes has bonus tracks but core album matches well
      score += 22; // High score for likely bonus track scenario
      console.log(`Detected likely bonus tracks: ${coreMatches}/${coreTrackCount} core tracks match (${Math.round(coreMatchRate * 100)}%)`);
    } else if (trackCountDiff <= 2) {
      score += 18; // Close enough
    } else if (trackCountDiff <= 4) {
      score += 10; // Somewhat close
    }

    // Core track matching rate - focus on the original album length (45 points)
    // This is the most important factor
    score += coreMatchRate * 45;

    // Duration similarity for core tracks (25 points)
    let durationScore = 0;
    let durationComparisons = 0;
    
    for (let i = 0; i < coreTrackCount; i++) {
      const originalTrack = originalTracks[i];
      const itunesTrack = itunesTracks[i];
      
      if (originalTrack.duration && itunesTrack.trackTimeMillis) {
        durationComparisons++;
        const originalSeconds = this.parseDurationToSeconds(originalTrack.duration);
        const itunesSeconds = Math.round(itunesTrack.trackTimeMillis / 1000);
        
        const durationDiff = Math.abs(originalSeconds - itunesSeconds);
        
        if (durationDiff <= 5) {
          durationScore += 1; // Very close
        } else if (durationDiff <= 15) {
          durationScore += 0.7; // Pretty close
        } else if (durationDiff <= 30) {
          durationScore += 0.3; // Somewhat close
        }
      }
    }
    
    if (durationComparisons > 0) {
      score += (durationScore / durationComparisons) * 25;
    } else {
      score += 10; // Partial credit if no duration data available
    }

    // Search result quality bonus (5 points)
    if (itunesTracks.length > 0) {
      score += 5; // Bonus for having iTunes results (implies artist/album match from search)
    }

    // Smart confidence threshold based on match quality
    let highConfidenceThreshold = 75;
    
    // Lower threshold for albums with excellent core matching
    if (coreMatchRate >= 0.75) {
      highConfidenceThreshold = 65; // Very good matching
    }
    
    // Even lower threshold for near-perfect albums with small track differences (likely bonus tracks/interludes)
    if (coreMatchRate >= 0.7 && hasExtraTracks && trackCountDiff <= 3) {
      highConfidenceThreshold = 60; // Likely bonus track or interlude scenario
      console.log(`Applying bonus track threshold: ${coreMatchRate * 100}% match rate with ${trackCountDiff} extra iTunes tracks`);
    }
    
    const highConfidence = score >= highConfidenceThreshold;
    
    console.log(`Album confidence details: ${coreMatches}/${coreTrackCount} core matches (${Math.round(coreMatchRate * 100)}%), ${trackCountDiff} track diff, score: ${Math.round(score)}`);
    
    return { score: Math.round(score), highConfidence };
  }

  private parseDurationToSeconds(duration: string): number {
    const parts = duration.split(':').map(p => parseInt(p, 10));
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    return 0;
  }

  private normalizeTrackPosition(position: string): number {
    // Convert vinyl positions (A1, A2, B1, B2, etc.) to sequential numbers
    // Also handle numeric positions (1, 2, 3) and complex ones (1-01, CD1-01)
    
    if (!position) return 0;
    
    // Handle pure numbers first
    const numMatch = position.match(/^\d+$/);
    if (numMatch) {
      return parseInt(numMatch[0], 10);
    }
    
    // Handle vinyl sides (A1, A2, B1, B2, C1, etc.)
    const vinylMatch = position.match(/^([A-Z])(\d+)$/);
    if (vinylMatch) {
      const side = vinylMatch[1];
      const trackNum = parseInt(vinylMatch[2], 10);
      const sideOffset = (side.charCodeAt(0) - 65) * 20; // A=0, B=20, C=40, etc.
      return sideOffset + trackNum;
    }
    
    // Handle CD-style (1-01, CD1-01, etc.)
    const cdMatch = position.match(/(\d+)[-.](\d+)/);
    if (cdMatch) {
      const disc = parseInt(cdMatch[1], 10);
      const track = parseInt(cdMatch[2], 10);
      return (disc - 1) * 20 + track;
    }
    
    // Handle other patterns by extracting the last number
    const lastNumMatch = position.match(/(\d+)$/);
    if (lastNumMatch) {
      return parseInt(lastNumMatch[1], 10);
    }
    
    return 0;
  }

  async enhanceTracksWithPreviews(
    tracks: Track[],
    artist: string,
    albumTitle: string,
    progressCallback?: (progress: number) => void
  ): Promise<Track[]> {
    if (!tracks.length) {
      console.log(`No tracks to enhance for ${artist} - ${albumTitle}`);
      return tracks;
    }

    try {
      progressCallback?.(10);
      
      // Search for the album on iTunes
      console.log(`Searching iTunes for: ${artist} - ${albumTitle}`);
      const itunesTracks = await this.searchAlbum(artist, albumTitle);
      
      if (!itunesTracks.length) {
        console.log(`No iTunes album found for: ${artist} - ${albumTitle}`);
        progressCallback?.(100);
        return tracks;
      }

      console.log(`Found iTunes album with ${itunesTracks.length} tracks`);
      console.log(`iTunes API order: ${itunesTracks.map((t, i) => `[${i}]"${t.trackName}"(#${t.trackNumber})`).join(', ')}`);
      
      // Sort iTunes tracks by track number to get correct album order
      itunesTracks.sort((a, b) => (a.trackNumber || 0) - (b.trackNumber || 0));
      console.log(`iTunes sorted order: ${itunesTracks.map((t, i) => `[${i}]"${t.trackName}"(#${t.trackNumber})`).join(', ')}`);
      
      progressCallback?.(30);

      // Debug: Check if any tracks have previews
      const tracksWithPreviews = itunesTracks.filter(t => t.previewUrl).length;
      console.log(`${tracksWithPreviews}/${itunesTracks.length} iTunes tracks have preview URLs`);

      progressCallback?.(50);

      // Calculate album-level confidence
      const albumConfidence = this.calculateAlbumConfidence(tracks, itunesTracks);
      console.log(`Album confidence score: ${albumConfidence.score} (${albumConfidence.highConfidence ? 'HIGH' : 'LOW'})`);

      progressCallback?.(60);

      // Step 1: Try to match tracks by title using all enhanced matching strategies
      // Search the FULL iTunes track list for each title, prioritizing title matching over position
      const titleMatches: Array<{ originalIndex: number; itunesIndex: number; track: iTunesTrackResult } | null> = [];
      const usedItunesTrackIds = new Set<number>(); // Prevent double-matching in title phase
      
      tracks.forEach((track, index) => {
        // Search all iTunes tracks for the best title match, regardless of position
        let bestMatch: { track: iTunesTrackResult; score: number } | null = null;
        
        for (const itunesTrack of itunesTracks) {
          // Skip already matched tracks
          if (usedItunesTrackIds.has(itunesTrack.trackId)) continue;
          
          if (this.trackTitlesMatch(track.title, itunesTrack.trackName, index + 1, itunesTrack.trackNumber)) {
            // Calculate match quality score - prioritize exact matches and prefer cleaner track names
            let score = 100;
            
            // Prefer non-remix/live/demo versions
            const hasModifiers = /\(.*(?:remix|live|demo|remaster|version|edit|dub|mix).*\)/i.test(itunesTrack.trackName);
            if (hasModifiers) score -= 20;
            
            // Prefer tracks with closer track numbers (might indicate main album vs bonus)
            const trackNumberDiff = Math.abs((itunesTrack.trackNumber || 999) - (index + 1));
            score -= Math.min(trackNumberDiff * 2, 40);
            
            if (!bestMatch || score > bestMatch.score) {
              bestMatch = { track: itunesTrack, score };
            }
          }
        }
        
        if (bestMatch) {
          const itunesIndex = itunesTracks.indexOf(bestMatch.track);
          titleMatches[index] = { originalIndex: index, itunesIndex, track: bestMatch.track };
          usedItunesTrackIds.add(bestMatch.track.trackId);
          console.log(`Title match: "${track.title}" -> "${bestMatch.track.trackName}" (score: ${bestMatch.score})`);
        } else {
          titleMatches[index] = null;
        }
      });
      
      // Step 2: Check if >2/3 of matches are in correct positional order using normalized positions
      const validMatches = titleMatches.filter(Boolean);
      const matchRate = validMatches.length / tracks.length;
      
      // Create a mapping of normalized original positions to iTunes positions
      const positionMappings = validMatches.map(match => match ? ({
        originalNormalized: this.normalizeTrackPosition(tracks[match.originalIndex].position),
        itunesNormalized: match.itunesIndex + 1, // iTunes uses 1-based indexing
        originalIndex: match.originalIndex,
        itunesIndex: match.itunesIndex
      }) : null).filter((mapping): mapping is NonNullable<typeof mapping> => 
        mapping !== null && mapping.originalNormalized > 0
      ); // Filter out position-less tracks and null values
      
      // Sort by original normalized position to check order consistency
      positionMappings.sort((a, b) => a.originalNormalized - b.originalNormalized);
      
      let positionallyConsistent = 0;
      for (let i = 0; i < positionMappings.length - 1; i++) {
        const current = positionMappings[i];
        const next = positionMappings[i + 1];
        // Check if iTunes positions are also in ascending order
        if (current.itunesNormalized < next.itunesNormalized) {
          positionallyConsistent++;
        }
      }
      
      const positionConsistencyRate = positionMappings.length > 1 ? positionallyConsistent / (positionMappings.length - 1) : 1;
      
      // Only use positional fill-in for high-confidence scenarios with excellent title matching
      // Be much more conservative to avoid bad matches from different album structures
      const usePositionalFillIn = matchRate >= 0.8 && albumConfidence.highConfidence && 
                                   (positionConsistencyRate >= 0.8 || validMatches.length >= tracks.length * 0.9);
      
      console.log(`Position mappings: ${positionMappings.map(p => `${tracks[p.originalIndex].position}(${p.originalNormalized})->iTunes${p.itunesNormalized}`).join(', ')}`);
      
      console.log(`Title matching: ${validMatches.length}/${tracks.length} (${Math.round(matchRate * 100)}%), positional consistency: ${Math.round(positionConsistencyRate * 100)}%, use fill-in: ${usePositionalFillIn}`);
      
      // Step 3: Build final enhanced tracks
      let matchedCount = 0;
      let previewsAdded = 0;
      
      const enhancedTracks = tracks.map((track, index) => {
        let matchingiTunesTrack = titleMatches[index]?.track;
        
        // If no title match but we can use positional fill-in, and we're within the iTunes track range
        if (!matchingiTunesTrack && usePositionalFillIn && index < itunesTracks.length) {
          const positionalTrack = itunesTracks[index];
          // Make sure this iTunes track isn't already used by a title match
          if (!usedItunesTrackIds.has(positionalTrack.trackId)) {
            console.log(`Positional fill-in: "${track.title}" -> "${positionalTrack.trackName}"`);
            matchingiTunesTrack = positionalTrack;
            usedItunesTrackIds.add(positionalTrack.trackId); // Mark as used
          }
        }

        if (matchingiTunesTrack) {
          matchedCount++;
          const hasPreview = !!matchingiTunesTrack.previewUrl;
          if (hasPreview) previewsAdded++;
          
          // Debug: log preview URL info for first few tracks
          if (matchedCount <= 3) {
            console.log(`Track "${track.title}" -> "${matchingiTunesTrack.trackName}", preview: ${matchingiTunesTrack.previewUrl || 'null'}`);
          }
          
          return {
            ...track,
            previewUrl: matchingiTunesTrack.previewUrl || undefined,
            itunesId: matchingiTunesTrack.trackId.toString(),
            duration: track.duration || this.formatDuration(matchingiTunesTrack.trackTimeMillis)
          };
        }

        return track;
      });

      console.log(`Matched ${matchedCount}/${tracks.length} tracks, added ${previewsAdded} previews`);
      if (albumConfidence.highConfidence) {
        console.log(`Used high-confidence album matching for better track coverage`);
      }
      
      progressCallback?.(100);
      return enhancedTracks;

    } catch (error) {
      console.error('Failed to enhance tracks with iTunes previews:', error);
      progressCallback?.(100);
      return tracks;
    }
  }

  private normalizeTrackTitle(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^\w\s]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private trackTitlesMatch(originalTitle: string, itunesTitle: string, originalTrackNum: number, itunesTrackNum?: number): boolean {
    const normalizeForMatch = (title: string) => title
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ') // Remove all non-alphanumeric except spaces
      .replace(/\b(feat|featuring|ft|remastered|remix|remaster|extended|radio edit|album version|single version)\b.*$/g, '') // Remove features and version info
      .replace(/\s+/g, ' ')
      .trim();
    
    const normalizedOriginal = normalizeForMatch(originalTitle);
    const normalizediTunes = normalizeForMatch(itunesTitle);
    
    // Debug logging for problematic matches
    if (originalTitle.includes('☁') || originalTitle.toLowerCase().includes('ripe') || itunesTitle.toLowerCase().includes('interlude') || 
        originalTitle.toLowerCase() === 'intro' || originalTitle.toLowerCase() === 'ms') {
      console.log(`Debug match: "${originalTitle}" -> "${itunesTitle}" (iTunes track #${itunesTrackNum || 'unknown'})`);
      console.log(`Normalized: "${normalizedOriginal}" -> "${normalizediTunes}"`);
    }
    
    // Exact match after normalization
    if (normalizedOriginal === normalizediTunes) return true;
    
    // Both titles must be reasonably long to avoid false matches (but allow "Ms" as exception)
    if ((normalizedOriginal.length < 3 || normalizediTunes.length < 3) && 
        !(normalizedOriginal === 'ms' && normalizediTunes === 'ms')) {
      return false;
    }
    
    // Special handling for tracks with parentheses content - extract the main content
    const extractMainContent = (title: string) => {
      const normalized = normalizeForMatch(title);
      const variants = [normalized];
      
      // If title has parentheses, try both the full title and just the parentheses content
      const parenthesesMatch = title.match(/\(([^)]+)\)/);
      if (parenthesesMatch) {
        variants.push(normalizeForMatch(parenthesesMatch[1]));
      }
      
      // Also try the title without parentheses content (e.g., "Neighbourhood #2 (Laïka)" -> "neighbourhood 2")
      const withoutParentheses = title.replace(/\([^)]*\)/g, '').trim();
      if (withoutParentheses !== title) {
        variants.push(normalizeForMatch(withoutParentheses));
      }
      
      // Try extracting just the main word(s) before parentheses for complex titles
      const beforeParentheses = title.split('(')[0].trim();
      if (beforeParentheses.length > 2 && beforeParentheses !== title) {
        variants.push(normalizeForMatch(beforeParentheses));
      }
      
      return variants;
    };
    
    const originalVariants = extractMainContent(originalTitle);
    const itunesVariants = extractMainContent(itunesTitle);
    
    // Try all combinations
    for (const origVar of originalVariants) {
      for (const itunesVar of itunesVariants) {
        // Exact match
        if (origVar === itunesVar) return true;
        
        // Contains match with length requirements
        if (origVar.length >= 4 && itunesVar.length >= 4) {
          if (origVar.includes(itunesVar) || itunesVar.includes(origVar)) {
            const shorter = origVar.length < itunesVar.length ? origVar : itunesVar;
            const longer = origVar.length >= itunesVar.length ? origVar : itunesVar;
            if (longer.includes(shorter) && shorter.length / longer.length >= 0.6) {
              return true;
            }
          }
        }
      }
    }
    
    // Special case: Generic interlude matching for highly descriptive vs generic titles
    const isOriginalInterludeType = /^(intro|interlude|outro|bridge|instrumental|guitar|piano|drums|vocal)$/i.test(normalizedOriginal) || 
                                    originalTitle.toLowerCase().includes('interlude') ||
                                    /^\w+\s*\([^)]*\)$/.test(originalTitle); // Pattern like "❦ (Guitar)"
    const isItunesInterludeType = /^(intro|interlude|outro|bridge|instrumental)\s*\d*$/i.test(normalizediTunes);
    
    if (isOriginalInterludeType && isItunesInterludeType) {
      console.log(`Potential interlude match: "${originalTitle}" vs "${itunesTitle}" - considering for high confidence albums`);
      // Don't match here directly, but this indicates we might want positional matching in high confidence scenarios
      return false; // Let high confidence positional matching handle this
    }
    
    // Track number match as fallback - but only for exact track number matches
    if (itunesTrackNum && itunesTrackNum === originalTrackNum) {
      // Still require some title similarity if using track numbers
      const originalWords = normalizedOriginal.split(' ');
      const itunesWords = normalizediTunes.split(' ');
      const commonWords = originalWords.filter(word => word.length > 2 && itunesWords.includes(word));
      return commonWords.length >= 1; // At least one meaningful word in common
    }
    
    return false;
  }

  private formatDuration(durationMs: number): string {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  isConfigured(): boolean {
    return true; // iTunes API doesn't require authentication
  }
}

export const itunesService = new iTunesService();